import { layerPropertiesAtom } from '@/atoms';
import {
  defaultLayerSettings,
  isLayerSelectedAtom,
  LayerData,
  layersAtom,
  layerSettingsAtom,
  layerSettingsStorageKey,
  selectedLayerIdsAtom,
  selectedLayersAtom,
  setLayersAtom,
  toggleLayerSelectionAtom,
} from '@/atoms/layers.atom';
import { userViewerSettingsAtom } from '@/atoms/user.atom';
import { addPointModeAtom } from '@/atoms/viewer.atom';
import { LayerSettingsDialog } from '@/components/LayerSettingsDialog';
import PortalTooltip from '@/components/PortalTooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { Layer, LayerType } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { cn } from '@/lib/utils';
import { useAlert } from '@/providers/AlertProvider';
import { useDialog } from '@/providers/DialogProvider';
import { deleteLayerMutation, layerSettingsQuery, layersQuery } from '@/queries/layer.query';
import {
  BoundingBoxSolid,
  BuildingSolid,
  CogFourSolid,
  CogOne,
  Edit,
  FolderSolid,
  ImageSolid,
  LayersThreeSolid,
  LockSolid,
  MapSolid,
  Plus,
  RadioSolid,
  RefreshAlt,
  Search,
  Table,
  Trash,
} from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LayerEditorContent } from '../ProjectLayers';

interface iLayersProps {
  projectId: string;
}

interface LayerItemProps {
  layer: LayerData & { subLayers: LayerData[] };
  onToggleLayer: (layerId: string) => void;
  onTurnOffLayer: (layerId: string) => void;
  isLayerSelected: (layerId: string) => boolean;
  isLayerIndeterminate: (layerId: string) => boolean;
  expandedLayers: Set<string>;
  onToggleExpanded: (layerId: string) => void;
  is3DDataAvailable: boolean;
  depth?: number;
}

export const LayerIcon = ({ type }: { type: LayerData['type'] }) => {
  switch (type) {
    case 'VECTOR':
      return <BoundingBoxSolid className='flex-shrink-0 size-4 text-fuchsia-700 dark:text-pink-500' />;
    case 'IMAGE':
      return <ImageSolid className='flex-shrink-0 size-4 text-sky-700 dark:text-sky-500' />;
    case 'POINTCLOUD':
      return <RadioSolid className='flex-shrink-0 size-4 stroke-2 text-green-500 dark:text-lime-500' />;
    case 'GROUP':
      return <FolderSolid className='flex-shrink-0 size-4 stroke-2 text-gray-700 dark:text-gray-300' />;
    case 'MODEL':
      return <BuildingSolid className='flex-shrink-0 size-4 text-orange-600 dark:text-amber-500' />;
    case 'TILE':
      return <MapSolid className='flex-shrink-0 size-4 text-orange-600 dark:text-amber-500' />;
  }
  return null;
};

const LayerItem: FunctionComponent<LayerItemProps> = ({
  layer,
  onToggleLayer,
  isLayerSelected,
  isLayerIndeterminate,
  is3DDataAvailable,
  expandedLayers,
  onToggleExpanded,
  onTurnOffLayer,
  depth = 0,
}) => {
  const navigate = useNavigate();
  const { organization, hasRole } = useOrganizations();
  const { projectId } = useParams();
  const dialog = useDialog();
  const alert = useAlert();
  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const setLayerProperties = useSetAtom(layerPropertiesAtom);
  const setLayerSettings = useSetAtom(layerSettingsAtom(layer.id));
  const client = useQueryClient();
  const [addPointMode, setAddPointMode] = useAtom(addPointModeAtom);
  const [isLocalSettings, setIsLocalSettings] = useState(false);
  const isAttributeTableEnabled = useFeatureFlagEnabled('attributeTable');

  const hasSubLayers = layer.subLayers.length > 0;
  const isExpanded = expandedLayers.has(layer.id);
  const indentClass = depth > 0 ? `ml-${depth * 4}` : '';
  const isIndeterminate = isLayerIndeterminate(layer.id);
  const isSelected = isLayerSelected(layer.id);

  const _handleOpenLayerSettings = () => {
    dialog.openDialog(<LayerSettingsDialog layer={layer} isEditor={hasRole('EDITOR')} />);
  };

  const _handleUseDefaultSettings = async () => {
    const data = await execute(layerSettingsQuery, {
      id: layer.id,
    });
    setLayerSettings(data.layer?.settings || layer.settings || defaultLayerSettings);
    setTimeout(() => {
      localStorage.removeItem(layerSettingsStorageKey(layer.id));
    }, 100);
  };

  const _handleOpenLayerProperties = () => {
    setLayerProperties(layer);
  };

  const _handleOpenAttributes = () => {
    navigate(`/projects/${projectId}/layers/${layer.id}/attributes`);
  };

  const _handleOpenLayerEditor = () => {
    dialog.openDialog(
      <LayerEditorContent
        projectId={projectId!}
        layer={layer}
        onSuccess={() => {
          client.invalidateQueries({ queryKey: ['layers', projectId] });
          client.invalidateQueries({ queryKey: ['layersNested', projectId] });
        }}
      />,
    );
  };

  const _handleDeleteLayer = (layerId: string) => {
    alert.openAlert({
      title: 'Delete layer?',
      content: 'This action cannot be undone. This will permanently delete the layer and all associated data.',
      actions: [
        {
          label: 'Delete Layer',
          variant: 'destructive',
          onClick: () => _handleExecuteDeleteLayer(layerId),
        },
      ],
    });
  };

  const _handleExecuteDeleteLayer = async (layerId: string) => {
    const toastId = toast.loading('Deleting layer...');
    try {
      await execute(deleteLayerMutation, { id: layerId });
      toast.success('Layer deleted', { id: toastId });
      client.invalidateQueries({ queryKey: ['layers', projectId] });
      client.invalidateQueries({ queryKey: ['layersNested', projectId] });
      onTurnOffLayer(layerId);
    } catch (error) {
      toast.error('Failed to delete layer', { id: toastId });
    }
  };

  const isAddPointActive = addPointMode.enabled && addPointMode.targetLayerId === layer.id;

  const _handleToggleAddPoint = () => {
    if (isAddPointActive) {
      // Deactivate add point mode
      setAddPointMode({ enabled: false, targetLayerId: null, draftFeature: null });
    } else {
      // Activate add point mode for this layer
      setAddPointMode({ enabled: true, targetLayerId: layer.id, draftFeature: null });
    }
  };

  return (
    <ContextMenu
      onOpenChange={(open) => {
        if (open) {
          setIsLocalSettings(!!window.localStorage.getItem(layerSettingsStorageKey(layer.id)));
        }
      }}>
      <ContextMenuTrigger>
        <div className={cn('select-none', depth > 0 && 'border-l border-gray-200 dark:border-stone-800 pl-3')}>
          <div
            className={cn(
              'flex flex-row items-center gap-1 py-1 pr-1.5 rounded-md transition-colors',
              indentClass,
              isAddPointActive && 'bg-orange-50 dark:bg-orange-950/20',
            )}>
            {hasSubLayers && (
              <button
                className='flex flex-shrink-0 items-center justify-center size-5 hover:bg-gray-100 dark:hover:bg-stone-800/50 rounded'
                onClick={() => onToggleExpanded(layer.id)}>
                <ChevronDown
                  className={cn(
                    'size-4 text-gray-600 stroke-2 stroke-current transition-all dark:text-white/70',
                    isExpanded ? 'rotate-0' : '-rotate-90',
                  )}
                />
              </button>
            )}

            <div className='flex items-center gap-1 mr-2 flex-1 overflow-hidden'>
              <div className='size-5 flex items-center justify-center'>
                <LayerIcon type={layer.type} />
              </div>

              <Label
                htmlFor={layer.id}
                className={cn('text-sm font-[450] flex-1 cursor-pointer line-clamp-1 overflow-hidden text-ellipsis')}>
                {layer.metadata?.source && viewerSettings.showLayerSource === true && (
                  <span className='text-xs font-semibold text-slate-700 dark:text-gray-200 bg-slate-100 border border-slate-200/70 dark:bg-stone-800 dark:border-stone-700 font-mono px-1.5 rounded-full mr-1'>
                    {layer.metadata?.source}
                  </span>
                )}
                {layer.name}
                {isAddPointActive && (
                  <span className='text-xs font-semibold text-orange-700 dark:text-orange-400 bg-orange-100 border border-orange-200/70 dark:bg-orange-900/30 dark:border-orange-800 px-1.5 rounded-full ml-1'>
                    Adding Points
                  </span>
                )}
              </Label>
            </div>
            {layer.type === 'VECTOR' && hasRole('EDITOR') && !layer.isLocked && is3DDataAvailable && (
              <button
                onClick={_handleToggleAddPoint}
                data-tooltip-id='layers-add-point'
                className={cn(
                  'size-6 rounded-md flex items-center justify-center transition-colors',
                  isAddPointActive
                    ? 'bg-orange-500 text-white hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800/50',
                )}
                title={isAddPointActive ? 'Stop adding points' : 'Add points to this layer'}>
                <Plus className='size-4 stroke-2' />
              </button>
            )}
            {layer.isLocked && (
              <LockSolid
                data-tooltip-id='layers-locked'
                className='size-4 stroke-2 text-slate-700 dark:text-stone-200/70 mr-1'
              />
            )}

            <Checkbox
              id={layer.id}
              checked={isSelected || isIndeterminate}
              onCheckedChange={() => onToggleLayer(layer.id)}
              indeterminate={isIndeterminate}
            />
          </div>

          {hasSubLayers && isExpanded && (
            <div className='pl-2.5 ml-px'>
              {layer.subLayers
                .filter((subLayer) => !subLayer.isHidden)
                .map((subLayer) => (
                  <LayerItem
                    key={subLayer.id}
                    layer={subLayer as LayerData & { subLayers: LayerData[] }}
                    is3DDataAvailable={is3DDataAvailable}
                    onToggleLayer={onToggleLayer}
                    onTurnOffLayer={onTurnOffLayer}
                    isLayerSelected={isLayerSelected}
                    isLayerIndeterminate={isLayerIndeterminate}
                    expandedLayers={expandedLayers}
                    onToggleExpanded={onToggleExpanded}
                    depth={depth + 1}
                  />
                ))}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem disabled className='cursor-default opacity-100' style={{ opacity: 1 }}>
          <span className='flex items-center gap-1'>
            <LayerIcon type={layer.type} />
            {layer.name}
          </span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuCheckboxItem checked={isSelected} onClick={() => onToggleLayer(layer.id)}>
          Show Layer
        </ContextMenuCheckboxItem>
        <ContextMenuItem disabled={layer.type === 'GROUP'} onClick={_handleOpenLayerProperties}>
          <Search className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          Layer Properties
        </ContextMenuItem>
        <ContextMenuItem disabled={layer.type === 'GROUP' || !isAttributeTableEnabled} onClick={_handleOpenAttributes}>
          <Table className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          Attributes
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem disabled={layer.type === 'GROUP'} onClick={_handleOpenLayerSettings}>
          <CogOne className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          Open Display Settings
        </ContextMenuItem>
        <ContextMenuItem onClick={_handleUseDefaultSettings} disabled={!isLocalSettings}>
          <RefreshAlt className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
          Reset Display Settings
        </ContextMenuItem>
        {hasRole('EDITOR') && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={_handleOpenLayerEditor}>
              <Edit className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              Open Layer Editor
            </ContextMenuItem>
            <ContextMenuItem onClick={() => _handleDeleteLayer(layer.id)}>
              <Trash className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              Delete Layer
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export const useLayers = ({ projectId }: { projectId: string }) => {
  const [layerList] = useAtom(layersAtom);
  const [selectedLayers] = useAtom(selectedLayersAtom(projectId));
  const [selectedLayerIds, setSelectedLayerIds] = useAtom(selectedLayerIdsAtom(projectId));
  const [, toggleLayerSelection] = useAtom(toggleLayerSelectionAtom(projectId));
  const [, updateLayers] = useAtom(setLayersAtom);
  const [isLayerSelected] = useAtom(isLayerSelectedAtom(projectId));

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
  });

  // Update the atom when data changes
  useEffect(() => {
    const layers = data?.layers;
    if (!layers) return;

    updateLayers(layers);
  }, [data?.layers, updateLayers]);

  // Separate effect for initial selection - only runs once when layers are first loaded
  useEffect(() => {
    const layers = data?.layers;
    if (!layers || selectedLayerIds.length > 0) return;

    const getLayerById = (layerId: string) => layers.find((layer) => layer.id === layerId);
    const isSelectableLayer = (layer: LayerData) => {
      if (layer.isHidden) return false;
      if (layer.type !== 'VECTOR' && layer.type !== 'IMAGE') return false;
      if (!layer.parentLayerId) return true;
      const parentLayer = getLayerById(layer.parentLayerId);
      return parentLayer?.isHidden !== true;
    };

    setSelectedLayerIds(layers.filter(isSelectableLayer).map((layer) => layer.id));
  }, [data?.layers, setSelectedLayerIds]);

  // Helper function to get all sublayer IDs recursively
  const getAllSubLayerIds = (layerId: string): string[] => {
    const subLayerIds: string[] = [];

    const findSubLayers = (parentId: string) => {
      const children = layerList.filter((layer) => layer.parentLayerId === parentId);
      children.forEach((child) => {
        subLayerIds.push(child.id);
        findSubLayers(child.id); // Recursively find grandchildren
      });
    };

    findSubLayers(layerId);
    return subLayerIds;
  };

  // Helper function to check if a layer should show indeterminate state (only for GROUP layers)
  const isLayerIndeterminate = (layerId: string): boolean => {
    const layer = layerList.find((l) => l.id === layerId);
    if (!layer || layer.type !== 'GROUP') return false;

    const allSubLayerIds = getAllSubLayerIds(layerId);

    // If no sublayers, not indeterminate
    if (allSubLayerIds.length === 0) return false;

    const selectedSubLayers = allSubLayerIds.filter((id) => isLayerSelected(id));

    // Indeterminate if some but not all sublayers are selected
    return selectedSubLayers.length > 0 && selectedSubLayers.length < allSubLayerIds.length;
  };

  // Wrapper to check if a layer is selected - for GROUP layers, derive from children
  const isLayerSelectedWithGroups = (layerId: string): boolean => {
    const layer = layerList.find((l) => l.id === layerId);
    if (!layer) return false;

    // For GROUP layers, check if ALL sublayers are selected
    if (layer.type === 'GROUP') {
      const allSubLayerIds = getAllSubLayerIds(layerId);
      if (allSubLayerIds.length === 0) return false;
      return allSubLayerIds.every((id) => isLayerSelected(id));
    }

    // For non-GROUP layers, use the original selection check
    return isLayerSelected(layerId);
  };

  const _handleToggleLayer = (layerId: string) => {
    const isCurrentlySelected = isLayerSelected(layerId);
    const allSubLayerIds = getAllSubLayerIds(layerId);

    if (isCurrentlySelected) {
      const layersToDeselect = [layerId, ...allSubLayerIds];
      layersToDeselect.forEach((id) => {
        if (isLayerSelected(id)) {
          toggleLayerSelection(id);
        }
      });
    } else {
      const layersToSelect = [layerId, ...allSubLayerIds];
      layersToSelect.forEach((id) => {
        if (!isLayerSelected(id)) {
          toggleLayerSelection(id);
        }
      });
    }
  };

  const _handleTurnOffLayer = (layerId: string) => {
    if (isLayerSelected(layerId)) {
      toggleLayerSelection(layerId);
    }
  };

  const _handleToggleLayerType = (type: LayerType) => {
    const layersForType = layerList.filter((layer) => layer.type === type) as Layer[];

    const isAllLayersSelected = layersForType.every((layer) => isLayerSelected(layer.id));

    if (isAllLayersSelected) {
      layersForType.forEach((layer) => {
        toggleLayerSelection(layer.id);
      });
    } else {
      layersForType.forEach((layer) => {
        if (!isLayerSelected(layer.id)) {
          toggleLayerSelection(layer.id);
        }
      });
    }
  };

  const layers = useMemo(() => {
    const buildLayerTree = (parentId: string | null): (LayerData & { subLayers: LayerData[] })[] => {
      return layerList
        .filter((layer) => layer.parentLayerId === parentId)
        .map((layer) => ({
          ...layer,
          subLayers: buildLayerTree(layer.id),
        }));
    };

    return buildLayerTree(null);
  }, [layerList]);

  return {
    layers,
    refetch,
    isLoading,
    enabledLayers: selectedLayers,
    onToggleLayer: _handleToggleLayer,
    onToggleLayerType: _handleToggleLayerType,
    onTurnOffLayer: _handleTurnOffLayer,
    isLayerSelected: isLayerSelectedWithGroups,
    isLayerIndeterminate,
  };
};

const Layers: FunctionComponent<iLayersProps> = ({ projectId }) => {
  const { organization, hasRole } = useOrganizations();
  const { layers, onToggleLayer, isLayerSelected, isLayerIndeterminate, onToggleLayerType, onTurnOffLayer } = useLayers(
    { projectId },
  );
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // const [searchTerm, setSearchTerm] = useState('');
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const isLayers = params.get('layers') === 'true';
  const viewerSettings = useAtomValue(userViewerSettingsAtom);

  const handleToggleExpanded = (layerId: string) => {
    setExpandedLayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }
      return newSet;
    });
  };

  const _handleOpenLayerSettings = () => {
    navigate(`/projects/${projectId}/layers`);
  };

  const layerTypes = Array.from(new Set(layers?.map((layer) => layer.type))).sort();

  const renderLayerContent = (layers: Layer[]) => {
    return layers
      .filter((layer) => !layer.isHidden)
      .map((layer) => (
        <LayerItem
          is3DDataAvailable={layers.some((l) => l.type === 'POINTCLOUD' && isLayerSelected(l.id))}
          key={layer.id}
          layer={layer}
          onToggleLayer={onToggleLayer}
          isLayerSelected={isLayerSelected}
          isLayerIndeterminate={isLayerIndeterminate}
          expandedLayers={expandedLayers}
          onToggleExpanded={handleToggleExpanded}
          onTurnOffLayer={onTurnOffLayer}
        />
      ));
  };
  return (
    <div
      style={{
        maxHeight: `calc(100vh - var(--spacing) * 15)`,
      }}
      className={cn(
        'w-72 overflow-hidden pt-3 text-left border-l border-gray-200 dark:border-stone-800 h-full flex flex-col',
        isLayers ? 'flex' : 'hidden',
      )}>
      <div className='flex items-center mb-4 px-3.5  justify-between'>
        <div className='flex flex-col leading-tight'>
          <span className='font-medium text-slate-800 dark:text-white flex items-center gap-1.5'>
            <LayersThreeSolid className='size-4' />
            Layers
          </span>
          <span className='text-xs text-slate-500 dark:text-white/70 font-medium'>Layers shown in the viewer</span>
        </div>
        {hasRole('EDITOR') && (
          <button
            onClick={_handleOpenLayerSettings}
            className='size-7 rounded-md flex items-center justify-center text-slate-700 hover:bg-slate-200/70 active:bg-slate-200 dark:text-stone-100 dark:hover:bg-stone-800/50 dark:active:bg-stone-800/50'>
            <CogFourSolid className='size-4' />
          </button>
        )}
      </div>

      <PortalTooltip delayShow={120} id='layers-type-vector'>
        Vector
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-locked'>
        Layer is locked
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-add-point'>
        Add points to layer
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-image'>
        Image
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-pointcloud'>
        Pointcloud
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-model'>
        Model
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-tile'>
        Tile
      </PortalTooltip>
      <PortalTooltip delayShow={120} id='layers-type-group'>
        Group
      </PortalTooltip>

      <div className='overflow-y-auto flex-1 flex flex-col px-3.5 pb-4'>
        {viewerSettings.showLayerUnderType
          ? layerTypes.map((type) => {
              const layersForType = layers.filter((layer) => layer.type === type) as Layer[];
              const isAllLayersSelected = layersForType.every((layer) => isLayerSelected(layer.id));
              const isAnyLayerSelected = layersForType.some((layer) => isLayerSelected(layer.id));
              return (
                <Collapsible key={type} defaultOpen className='mb-4 group/type'>
                  <div className='flex items-center justify-between mb-2 pr-1.5'>
                    <CollapsibleTrigger className='flex items-center gap-1 cursor-pointer'>
                      <div className='flex items-center justify-center size-5'>
                        <ChevronRight className='size-4 text-gray-700 dark:text-gray-400 stroke-2 transition-transform duration-200 group-data-[state=open]/type:rotate-90 transition-all' />
                      </div>
                      <span className='text-xs font-semibold text-slate-700 dark:text-white'>{type}</span>
                    </CollapsibleTrigger>
                    <Checkbox
                      checked={isAnyLayerSelected}
                      indeterminate={!isAllLayersSelected}
                      onCheckedChange={() => {
                        onToggleLayerType(type);
                      }}
                    />
                  </div>
                  <CollapsibleContent>{renderLayerContent(layersForType)}</CollapsibleContent>
                </Collapsible>
              );
            })
          : renderLayerContent(layers as Layer[])}
        {}
      </div>
    </div>
  );
};

export default memo(Layers);
